'use client'

import ExamPicker, { SelectedExam } from './exam-picker'
import TemplateApplyBar from './template-apply-bar'
import SaveAsTemplateModal from './save-as-template-modal'
import {
    CreateTemplateItemData,
    TemplateCategory,
} from '@/lib/api/templates/template-api'
import { FormField, FormSection } from '@/components/form-section'
import PaginationControls from '@/components/pagination/PaginationControls'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    addCatalogVariation,
    Catalog,
    CatalogVariation,
    getCatalogsByCompany,
    getCatalogVariations,
} from '@/lib/api/catalog/catalog-api'
import { createExam } from '@/lib/api/exams/exam-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdArrowForward,
    MdBiotech,
    MdBookmarkAdd,
    MdCheck,
    MdCheckCircle,
    MdClose,
    MdDownload,
    MdEmail,
    MdPrint,
    MdDelete,
    MdExpandLess,
    MdExpandMore,
    MdNotes,
    MdPerson,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    clearExamSuggestions,
    readExamSuggestions,
} from '@/lib/medical/exam-suggestions'
import { useRecordActions } from '@/lib/medical/use-record-actions'
import { getCatalog } from '@/lib/api/catalog/catalog-api'

/** Item escolhido para o pedido, com as variações marcadas nele. */
interface Props {
    clientId: number
    clientName?: string
    /** E-mail do paciente: sem ele, a ação de enviar o pedido aparece desabilitada. */
    clientEmail?: string | null
    onClose: () => void
    /** Pedido criado: a tela que abriu recarrega a lista dela. */
    onCreated?: (examId: number) => void
}

/**
 * Pedido de exames — a ordem que o paciente leva ao laboratório.
 *
 * NÃO é a tela de agendamento: aqui não há preço, parceiro, forma de pagamento nem
 * cobrança, porque quem pede o exame não é quem o vende. A tela de agendamento
 * continua existindo para quando a própria clínica executa e cobra.
 *
 * O catálogo é o mesmo (`Catalog` categoria EXAM, com as variações-como-item): o
 * exame cadastrado no meio de uma consulta fica disponível para o próximo paciente,
 * que é o motivo de o cadastro rápido morar aqui dentro em vez de mandar o médico
 * para a tela de catálogo.
 *
 * Duas etapas de propósito. Escolher exames é uma tarefa de busca — o médico varre a
 * lista, marca cinco, muda de página — e misturar o formulário do pedido com essa
 * varredura empurra as anotações para fora da tela justamente quando ele termina de
 * marcar. "Avançar" fecha a busca e mostra o pedido inteiro numa página só.
 */
export default function ExamRequestModal({
    clientId,
    clientName,
    clientEmail,
    onClose,
    onCreated,
}: Props) {
    const { user } = useAuth()
    const companyId = user?.company?.id

    const [step, setStep] = useState<'select' | 'details' | 'done'>('select')
    /**
     * Pedido criado. O modal NÃO fecha ao concluir: é neste instante que o médico
     * quer imprimir, baixar ou mandar ao paciente, e fechar o obrigaria a procurar
     * o pedido numa lista para fazer o que já ia fazer.
     */
    const [createdExamId, setCreatedExamId] = useState<number | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const debouncedSearch = useDebounce(searchTerm, 400)
    const [selected, setSelected] = useState<SelectedExam[]>([])
    const [notes, setNotes] = useState('')
    const [professionalId, setProfessionalId] = useState<number | null>(
        user?.professionalId ?? null
    )
    const [submitting, setSubmitting] = useState(false)
    const [showQuickCreate, setShowQuickCreate] = useState(false)
    /**
     * Exames cadastrados nesta sessão do pedido.
     *
     * Ficam fixos no topo da lista porque a listagem é alfabética e paginada: o item
     * recém-criado costuma cair em outra página, e o médico via a tela voltar ao que
     * era — parecia que o cadastro não tinha funcionado.
     */
    const [createdItems, setCreatedItems] = useState<Catalog[]>([])
    /**
     * O pedido nasceu de um modelo?
     *
     * Só oferecemos "salvar como modelo" para o que foi montado à mão: quem aplicou
     * um modelo e emitiu sem mudar nada já tem o modelo, e o convite viraria ruído
     * — ou pior, uma cópia duplicada com outro nome.
     */
    const [appliedTemplateId, setAppliedTemplateId] = useState<number | null>(
        null
    )

    /**
     * Sugestões da IA que não acharam item no catálogo. Ficam em tela para o médico
     * cadastrar ou buscar à mão — sumir em silêncio faria o pedido sair sem o exame
     * que ele marcou na consulta, que é justamente o erro que a pré-seleção evita.
     */
    const [unmatchedSuggestions, setUnmatchedSuggestions] = useState<string[]>(
        []
    )
    const [resolvingSuggestions, setResolvingSuggestions] = useState(false)

    // Variações por item, carregadas sob demanda: a lista tem 10 itens por página e
    // buscar as variações de todos seria dez chamadas para uma que o médico abre.
    const [expandedId, setExpandedId] = useState<number | null>(null)
    const [variationsByCatalog, setVariationsByCatalog] = useState<
        Record<number, CatalogVariation[]>
    >({})
    const [loadingVariations, setLoadingVariations] = useState<number | null>(
        null
    )
    const [newVariationName, setNewVariationName] = useState('')
    const [creatingVariation, setCreatingVariation] = useState(false)

    const fetchFn = useCallback(
        (params: any) => getCatalogsByCompany(companyId!, params),
        [companyId]
    )

    const filters = useMemo(
        () => ({
            category: 'EXAM' as const,
            activeOnly: true,
            search: debouncedSearch.trim() || undefined,
            // Listagem, não agendamento: sem isto a busca esconde o que não tem
            // parceiro atrelado — e pedido médico não tem parceiro nenhum.
            isListing: true,
        }),
        [debouncedSearch]
    )

    const { items, loading, page, limit, total, setPage } = usePaginatedPage<
        Catalog,
        typeof filters
    >({
        fetchFn,
        filters,
        limit: 10,
        enabled: !!companyId && step === 'select',
    })

    const listItems = useMemo(
        () => [
            ...createdItems,
            ...items.filter(
                item => !createdItems.some(created => created.id === item.id)
            ),
        ],
        [createdItems, items]
    )

    /**
     * Ao abrir, transforma as sugestões que o médico marcou na análise da consulta
     * em itens do catálogo já selecionados.
     *
     * O casamento é feito pela BUSCA do catálogo, que é ranqueada e tolera acento e
     * erro de digitação — a IA escreve "Hemograma completo" lendo a transcrição, sem
     * saber como o item está cadastrado. Só o primeiro resultado é aceito: é o de
     * maior relevância, e escolher entre parecidos é decisão do médico, não nossa.
     *
     * Roda uma vez por abertura (`resolvedRef`): reexecutar sobrescreveria o que ele
     * marcou ou desmarcou à mão desde então.
     */
    const resolvedRef = useRef(false)

    useEffect(() => {
        if (!companyId || resolvedRef.current) return

        const suggestions = readExamSuggestions(clientId)
        if (suggestions.length === 0) return

        resolvedRef.current = true
        let active = true

        const resolve = async () => {
            setResolvingSuggestions(true)
            try {
                const results = await Promise.all(
                    suggestions.map(async suggestion => {
                        // Sugestão que já veio com `catalogId` (a IA resolveu pela
                        // ferramenta de busca) vai direto ao item — sem repetir a
                        // busca e sem o risco de o ranking devolver outro exame.
                        if (suggestion.catalogId) {
                            const byId = await getCatalog(suggestion.catalogId)
                            if (byId.success && byId.result) {
                                return { suggestion, match: byId.result }
                            }
                        }

                        const response = await getCatalogsByCompany(companyId, {
                            category: 'EXAM',
                            activeOnly: true,
                            isListing: true,
                            search: suggestion.term,
                            limit: 1,
                        })
                        const match =
                            response.success && Array.isArray(response.result)
                                ? response.result[0]
                                : undefined
                        return { suggestion, match }
                    })
                )

                if (!active) return

                const matched: SelectedExam[] = []
                const unmatched: string[] = []

                for (const { suggestion, match } of results) {
                    if (
                        match &&
                        !matched.some(item => item.catalog.id === match.id)
                    ) {
                        matched.push({ catalog: match, variations: [] })
                    } else if (!match) {
                        unmatched.push(suggestion.term)
                    }
                }

                if (matched.length > 0) {
                    setSelected(previous => [
                        ...previous,
                        ...matched.filter(
                            item =>
                                !previous.some(
                                    chosen =>
                                        chosen.catalog.id === item.catalog.id
                                )
                        ),
                    ])
                }
                setUnmatchedSuggestions(unmatched)

                if (matched.length > 0) {
                    toast.info(
                        matched.length === 1
                            ? '1 exame sugerido pela IA já veio marcado'
                            : `${matched.length} exames sugeridos pela IA já vieram marcados`
                    )
                }
            } finally {
                if (active) setResolvingSuggestions(false)
            }
        }

        resolve()

        return () => {
            active = false
        }
    }, [companyId, clientId])

    /** Tira um exame na etapa de REVISÃO; marcar e desmarcar é com o picker. */
    const removeItem = (catalogId: number) => {
        setSelected(previous =>
            previous.filter(item => item.catalog.id !== catalogId)
        )
    }

    const handleSubmit = async () => {
        if (!companyId) return
        if (selected.length === 0) {
            toast.error('Selecione ao menos um exame')
            return
        }

        setSubmitting(true)
        try {
            const response = await createExam({
                clientId,
                companyId,
                // Solicitante, não executante: é o que o PDF do pedido imprime.
                professionalId: professionalId ?? undefined,
                status: 'REQUESTED',
                requestOnly: true,
                notes: notes.trim() || undefined,
                items: selected.map(item => ({
                    catalogId: item.catalog.id,
                    quantity: 1,
                    variationCatalogIds: item.variations.map(v => v.id),
                })),
            })

            if (response.success && response.result) {
                // O rascunho cumpriu o papel: virou pedido.
                clearExamSuggestions(clientId)
                toast.success(
                    selected.length === 1
                        ? 'Exame solicitado'
                        : `${selected.length} exames solicitados`
                )
                setCreatedExamId(response.result.id)
                setStep('done')
                // A lista de quem abriu já recarrega agora: quando o médico fechar,
                // o pedido novo tem de estar lá, tenha ele usado as ações ou não.
                onCreated?.(response.result.id)
            } else {
                toast.error(response.error || 'Erro ao solicitar exames')
            }
        } catch (error) {
            toast.error('Erro ao solicitar exames')
        } finally {
            setSubmitting(false)
        }
    }

    const selectedCountLabel =
        selected.length === 1
            ? '1 exame selecionado'
            : `${selected.length} exames selecionados`

    return (
        <>
            {/*
             * Overlay próprio pelo mesmo motivo do modal do prontuário: `modal-box` do
             * DaisyUI aplica transform, e isso prenderia o cadastro rápido (que é um
             * `.modal` fixed) dentro desta caixa. z-index 910: acima do modal do
             * prontuário (900), abaixo do `.modal` do DaisyUI (999).
             */}
            <div
                className="fixed inset-0 z-[910] flex items-center justify-center bg-black/40 p-4"
                role="dialog"
                aria-modal="true"
                onClick={event => {
                    if (event.target === event.currentTarget) onClose()
                }}
            >
                <div className="w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden rounded-2xl border border-base-300/50 bg-base-100 shadow-xl">
                    {/* Cabeçalho */}
                    <div className="flex items-start justify-between gap-3 border-b border-base-300/60 px-6 py-4">
                        <div className="min-w-0">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                <MdBiotech className="text-secondary" />
                                Solicitar exames
                            </h3>
                            <p className="text-sm text-base-content/60 truncate">
                                {step === 'select'
                                    ? `Escolha os exames${
                                          clientName
                                              ? ` para ${clientName}`
                                              : ''
                                      }`
                                    : step === 'details'
                                      ? 'Confira o pedido antes de emitir'
                                      : 'Pedido emitido'}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Duas etapas, ditas por extenso: sem isto o "Avançar"
                                parece salvar. */}
                            <ol className="hidden sm:flex items-center gap-2 text-xs">
                                <li
                                    className={
                                        step === 'select'
                                            ? 'font-semibold text-secondary'
                                            : 'text-base-content/50'
                                    }
                                >
                                    1. Exames
                                </li>
                                <li className="text-base-content/30">›</li>
                                <li
                                    className={
                                        step === 'details'
                                            ? 'font-semibold text-secondary'
                                            : 'text-base-content/50'
                                    }
                                >
                                    2. Pedido
                                </li>
                                <li className="text-base-content/30">›</li>
                                <li
                                    className={
                                        step === 'done'
                                            ? 'font-semibold text-success'
                                            : 'text-base-content/50'
                                    }
                                >
                                    3. Pronto
                                </li>
                            </ol>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={onClose}
                                aria-label="Fechar"
                            >
                                <MdClose />
                            </button>
                        </div>
                    </div>

                    {step === 'select' ? (
                        <>
                            <ExamPicker
                                selected={selected}
                                onSelectedChange={setSelected}
                                createdItems={createdItems}
                                onItemCreated={catalog => {
                                    setCreatedItems(previous => [
                                        catalog,
                                        ...previous,
                                    ])
                                }}
                                banner={
                                    <>
                                        <TemplateApplyBar
                                            professionalId={professionalId}
                                            clientId={clientId}
                                            onApply={(
                                                exams,
                                                template,
                                                appliedNotes
                                            ) => {
                                                setAppliedTemplateId(
                                                    template.id
                                                )
                                                // SOMA ao que já está marcado: o médico
                                                // parte do modelo e acrescenta o que
                                                // aquele paciente precisa.
                                                setSelected(previous => [
                                                    ...previous,
                                                    ...exams.filter(
                                                        exam =>
                                                            !previous.some(
                                                                item =>
                                                                    item.catalog
                                                                        .id ===
                                                                    exam.catalog
                                                                        .id
                                                            )
                                                    ),
                                                ])
                                                // A indicação clínica do modelo só
                                                // entra se o médico ainda não escreveu
                                                // a dele — sobrescrever apagaria texto
                                                // digitado.
                                                if (
                                                    appliedNotes &&
                                                    !notes.trim()
                                                ) {
                                                    setNotes(appliedNotes)
                                                }
                                            }}
                                        />

                                        {resolvingSuggestions && (
                                            <div className="border-b border-base-300/60 px-6 py-2 text-sm text-base-content/60 flex items-center gap-2">
                                                <span className="loading loading-spinner loading-xs" />
                                                Marcando os exames sugeridos na
                                                consulta...
                                            </div>
                                        )}

                                        {unmatchedSuggestions.length > 0 && (
                                            <div className="border-b border-base-300/60 px-6 py-3">
                                                <div className="alert alert-warning text-sm">
                                                    <span className="whitespace-normal">
                                                        Sem item no catálogo
                                                        para:{' '}
                                                        <strong>
                                                            {unmatchedSuggestions.join(
                                                                ', '
                                                            )}
                                                        </strong>
                                                        . Busque com outro nome
                                                        ou cadastre pelo botão
                                                        ao lado da busca.
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                }
                            />

                            {/* Ações da etapa 1 */}
                            <div className="flex items-center justify-between gap-3 border-t border-base-300/60 px-6 py-4">
                                {/* Único indicador do que já foi marcado nesta etapa:
                                    a fila de chips que existia aqui virava parede com os
                                    20+ exames de um pedido real, e cada item marcado já
                                    se identifica na lista. A conferência do pedido
                                    inteiro é a etapa 2. */}
                                <span className="text-sm text-base-content/60">
                                    {selected.length === 0
                                        ? 'Nenhum exame selecionado'
                                        : selectedCountLabel}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={onClose}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm gap-2"
                                        onClick={() => setStep('details')}
                                        disabled={selected.length === 0}
                                    >
                                        Avançar
                                        <MdArrowForward className="text-base" />
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : step === 'done' && createdExamId ? (
                        <ExamRequestDone
                            examId={createdExamId}
                            clientEmail={clientEmail}
                            clientName={clientName}
                            examCount={selected.length}
                            fromTemplate={appliedTemplateId !== null}
                            templateItems={selected.map((item, index) => ({
                                catalogId: item.catalog.id,
                                variationCatalogIds: item.variations.map(
                                    v => v.id
                                ),
                                sortOrder: index,
                            }))}
                            templateNotes={notes}
                            professionalId={professionalId}
                            onClose={onClose}
                        />
                    ) : (
                        <>
                            {/* Etapa 2: o pedido inteiro numa página só */}
                            <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                                <FormSection
                                    icon={<MdBiotech className="text-lg" />}
                                    title="Exames solicitados"
                                    subtitle={selectedCountLabel}
                                    bodyClassName="flex flex-col gap-2 p-5"
                                    actions={
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs gap-1"
                                            onClick={() => setStep('select')}
                                        >
                                            <MdAdd className="text-base" />
                                            Adicionar mais
                                        </button>
                                    }
                                >
                                    {selected.map(item => (
                                        <div
                                            key={item.catalog.id}
                                            className="flex items-center gap-3 rounded-xl border border-base-300/50 p-3"
                                        >
                                            <span className="min-w-0 flex-1">
                                                <span className="block font-medium text-base-content truncate">
                                                    {item.catalog.name}
                                                </span>
                                                {item.variations.length > 0 ? (
                                                    <span className="block text-xs text-secondary">
                                                        {item.variations
                                                            .map(v => v.name)
                                                            .join(', ')}
                                                    </span>
                                                ) : (
                                                    item.catalog.tussCode && (
                                                        <span className="block text-xs text-base-content/50">
                                                            TUSS{' '}
                                                            {
                                                                item.catalog
                                                                    .tussCode
                                                            }
                                                        </span>
                                                    )
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                                onClick={() =>
                                                    removeItem(item.catalog.id)
                                                }
                                                title="Remover do pedido"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    ))}
                                    {selected.length === 0 && (
                                        <p className="text-sm text-base-content/60">
                                            Nenhum exame no pedido. Volte e
                                            selecione ao menos um.
                                        </p>
                                    )}
                                </FormSection>

                                <FormSection
                                    icon={<MdPerson className="text-lg" />}
                                    title="Solicitante"
                                    subtitle="Quem assina o pedido"
                                >
                                    <ProfessionalSearchSelector
                                        value={professionalId}
                                        onChange={id => setProfessionalId(id)}
                                        label="Profissional solicitante"
                                        placeholder="Buscar profissional..."
                                    />
                                </FormSection>

                                <FormSection
                                    icon={<MdNotes className="text-lg" />}
                                    title="Observações"
                                    subtitle="Indicação clínica e preparo do paciente"
                                >
                                    <FormField
                                        label="Anotações"
                                        htmlFor="pedido-exame-observacoes"
                                        optional
                                        hint="Sai no pedido impresso: jejum, indicação clínica, hipótese diagnóstica."
                                    >
                                        <textarea
                                            id="pedido-exame-observacoes"
                                            className="textarea textarea-bordered w-full leading-relaxed"
                                            placeholder="Anotações sobre o pedido..."
                                            value={notes}
                                            onChange={event =>
                                                setNotes(event.target.value)
                                            }
                                            rows={4}
                                        />
                                    </FormField>
                                </FormSection>
                            </div>

                            {/* Ações da etapa 2 */}
                            <div className="flex items-center justify-between gap-3 border-t border-base-300/60 px-6 py-4">
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm gap-2"
                                    onClick={() => setStep('select')}
                                    disabled={submitting}
                                >
                                    <MdArrowBack className="text-base" />
                                    Voltar
                                </button>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={onClose}
                                        disabled={submitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm gap-2"
                                        onClick={handleSubmit}
                                        disabled={
                                            submitting || selected.length === 0
                                        }
                                    >
                                        {submitting ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdCheck className="text-base" />
                                        )}
                                        Emitir pedido
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    )
}

interface ButtonProps {
    clientId?: number
    clientName?: string
    clientEmail?: string | null
    className?: string
    label?: string
    onCreated?: (examId: number) => void
}

/**
 * Botão "Solicitar exames" pronto para o cabeçalho das telas de atendimento.
 *
 * Some fora da saúde e sem paciente: pedido de exame é ato médico, e não há a quem
 * pedir sem paciente. Existe para o call site ser uma linha — a consulta, a sala de
 * atendimento e o prontuário abrem exatamente o mesmo pedido.
 */
export function ExamRequestButton({
    clientId,
    clientName,
    clientEmail,
    className = 'btn btn-outline btn-sm gap-2',
    label = 'Solicitar exames',
    onCreated,
}: ButtonProps) {
    const { isHealth } = useServiceLabel()
    const [open, setOpen] = useState(false)

    if (!isHealth || !clientId) return null

    return (
        <>
            <button
                type="button"
                className={className}
                onClick={() => setOpen(true)}
                title="Emitir pedido de exames para este paciente"
            >
                <MdBiotech className="text-lg" />
                <span className="hidden sm:inline">{label}</span>
            </button>
            {open && (
                <ExamRequestModal
                    clientId={clientId}
                    clientName={clientName}
                    clientEmail={clientEmail}
                    onClose={() => setOpen(false)}
                    onCreated={onCreated}
                />
            )}
        </>
    )
}

interface ExamRequestDoneProps {
    examId: number
    clientEmail?: string | null
    clientName?: string
    examCount: number
    /** Veio de um modelo: não faz sentido oferecer salvar como modelo de novo. */
    fromTemplate: boolean
    templateItems: CreateTemplateItemData[]
    templateNotes?: string
    professionalId?: number | null
    onClose: () => void
}

/**
 * Etapa 3: o pedido foi emitido e o médico ainda está aqui.
 *
 * Antes o modal fechava no "Emitir", e imprimir ou mandar ao paciente exigia
 * procurar o pedido numa lista para abrir a tela de detalhe — o caminho mais longo
 * possível para o que ele ia fazer em seguida.
 */
function ExamRequestDone({
    examId,
    clientEmail,
    clientName,
    examCount,
    fromTemplate,
    templateItems,
    templateNotes,
    professionalId,
    onClose,
}: ExamRequestDoneProps) {
    const [showSaveTemplate, setShowSaveTemplate] = useState(false)
    const [savedAsTemplate, setSavedAsTemplate] = useState(false)
    const { handlePdf, handleSendEmail, printing, sending, hasEmail } =
        useRecordActions({
            kind: 'exam',
            id: examId,
            clientEmail,
        })

    return (
        <>
            <div className="flex-1 overflow-y-auto px-6 py-8">
                <div className="flex flex-col items-center gap-3 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                        <MdCheckCircle className="text-4xl text-success" />
                    </div>
                    <div>
                        <p className="text-lg font-semibold text-base-content">
                            {examCount === 1
                                ? 'Exame solicitado'
                                : `${examCount} exames solicitados`}
                        </p>
                        <p className="text-sm text-base-content/60">
                            O pedido de{clientName ? ` ${clientName}` : ''} já
                            está no prontuário.
                        </p>
                    </div>
                </div>

                {/* Convite a virar modelo. Só para o pedido montado à mão, e some
                    depois de salvo: repetir o convite sobre um pedido que já virou
                    modelo só produziria cópias com nomes diferentes. */}
                {!fromTemplate &&
                    !savedAsTemplate &&
                    templateItems.length > 0 && (
                        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                            <div className="text-sm">
                                <p className="font-medium text-base-content">
                                    Costuma pedir esses exames juntos?
                                </p>
                                <p className="text-base-content/60">
                                    Salve como modelo e marque tudo num clique
                                    da próxima vez.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm gap-2"
                                onClick={() => setShowSaveTemplate(true)}
                            >
                                <MdBookmarkAdd className="text-base" />
                                Salvar como modelo
                            </button>
                        </div>
                    )}

                <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
                    <button
                        type="button"
                        className="btn btn-outline gap-2"
                        onClick={() => handlePdf('print')}
                        disabled={printing}
                    >
                        <MdPrint className="text-lg" />
                        Imprimir
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline gap-2"
                        onClick={() => handlePdf('download')}
                        disabled={printing}
                    >
                        <MdDownload className="text-lg" />
                        Baixar PDF
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline gap-2"
                        onClick={handleSendEmail}
                        disabled={sending || !hasEmail}
                        title={
                            hasEmail
                                ? undefined
                                : 'Paciente sem e-mail cadastrado'
                        }
                    >
                        <MdEmail className="text-lg" />
                        {sending ? 'Enviando...' : 'Enviar por e-mail'}
                    </button>
                </div>

                {!hasEmail && (
                    <p className="mt-3 text-center text-sm text-base-content/60">
                        Para enviar por e-mail, cadastre o e-mail do paciente.
                    </p>
                )}
            </div>

            <div className="border-t border-base-300/60 px-6 py-4 flex justify-end">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                >
                    Concluir
                </button>
            </div>
            <SaveAsTemplateModal
                isOpen={showSaveTemplate}
                onClose={() => setShowSaveTemplate(false)}
                category={TemplateCategory.EXAM_REQUEST}
                items={templateItems}
                notes={templateNotes}
                professionalId={professionalId}
                onSaved={() => setSavedAsTemplate(true)}
            />
        </>
    )
}
